import { AppShell, PageTitle } from "@/components/app-shell";
import { ErrorBoundary } from "@/components/error-boundary";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { ApiError, getHardware, getMindScene } from "@/lib/api";
import { projectHasHardwareRevision } from "@/lib/materials";
import { DEMO_SCENE, isMindScene } from "@/lib/scene";
import { scopeLoadKey, useKeyedLoad } from "@/lib/scope-loading";
import { requestOpenThink } from "@/lib/underline-nav";
import { MindCanvas } from "@rscircuit/mind-canvas";
import type { MindScene } from "@rscircuit/mind-canvas";
import "@rscircuit/mind-canvas/styles.css";
import "@xyflow/react/dist/style.css";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

type PanelStatus = "loading" | "empty" | "error" | "live" | "demo";

const EMPTY_COPY =
  "No mind scene yet — add design.mind and ask Think to compile it (mind_scene), or wait for a cached scene.";

/** Shared Circuit Mind canvas — used by Agents (and the legacy Mind page). */
export function MindCanvasPanel({ showEmptyCtaCard = false }: { showEmptyCtaCard?: boolean } = {}) {
  const { orgSlug, projectSlug } = useParams();
  const inProject = Boolean(orgSlug && projectSlug);
  const [prototype, setPrototype] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [status, setStatus] = useState<PanelStatus>(inProject ? "loading" : "demo");
  const [scene, setScene] = useState<MindScene | null>(inProject ? null : DEMO_SCENE);
  const [emptyMessage, setEmptyMessage] = useState(EMPTY_COPY);
  const [hasDesignMind, setHasDesignMind] = useState(false);
  const [hasRevision, setHasRevision] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const routeKey = scopeLoadKey(orgSlug, projectSlug);
  const { start, finish } = useKeyedLoad(routeKey);

  useEffect(() => {
    if (!inProject || !orgSlug || !projectSlug) {
      setStatus("demo");
      setScene(DEMO_SCENE);
      setErr(null);
      setHasDesignMind(false);
      setHasRevision(false);
      return;
    }
    const ac = new AbortController();
    const isCurrent = start();
    setStatus("loading");
    setScene(null);
    setErr(null);
    setHasDesignMind(false);
    setHasRevision(false);
    void (async () => {
      try {
        const [res, hw] = await Promise.all([
          getMindScene(orgSlug, projectSlug),
          getHardware(orgSlug, projectSlug).catch(() => ({ revisions: [] })),
        ]);
        if (ac.signal.aborted || !isCurrent()) return;
        setHasRevision(projectHasHardwareRevision(hw));
        if (res.ok && isMindScene(res.scene)) {
          setScene(res.scene);
          setStatus("live");
          return;
        }
        if (res.ok) {
          setStatus("error");
          setErr("Workbench returned JSON that is not mind-scene/1.1");
          return;
        }
        if (res.status === 404 || res.error === "no_mind_scene") {
          setHasDesignMind(res.has_design_mind === true);
          setEmptyMessage(res.message || EMPTY_COPY);
          setStatus("empty");
          return;
        }
        setStatus("error");
        setErr(res.message || `mind scene ${res.status}`);
      } catch (e) {
        if (ac.signal.aborted || !isCurrent()) return;
        setStatus("error");
        setErr(
          e instanceof ApiError
            ? e.message
            : e instanceof Error
              ? e.message
              : "mind scene load failed",
        );
      } finally {
        if (!ac.signal.aborted) finish(isCurrent);
      }
    })();
    return () => ac.abort();
  }, [inProject, orgSlug, projectSlug, start, finish]);

  const showDemo = status === "demo" && !inProject;
  const liveScene = status === "live" ? scene : showDemo ? DEMO_SCENE : null;
  const showGenerateCtas = status === "empty" && inProject && !hasDesignMind && hasRevision;

  return (
    <div data-testid="mind-canvas-panel">
      {showEmptyCtaCard && showGenerateCtas && (
        <div
          className="mb-4 rounded-md border border-border bg-card px-4 py-3"
          data-testid="agents-mind-empty-cta"
        >
          <p className="m-0 text-[13px] font-medium text-foreground">No design.mind on HEAD yet.</p>
          <p className="mt-1 mb-3 text-[13px] text-muted-foreground">
            This project has a hosted hardware revision. Open Think to generate design.mind from the
            schematic — there is no separate distill tool in this UI.
          </p>
          <Button size="sm" onClick={() => requestOpenThink()}>
            Open Think
          </Button>
        </div>
      )}
      <div className="mb-3 flex items-center gap-2 text-sm">
        <span className="text-muted-foreground">Read-only projection</span>
        <Switch checked={prototype} onCheckedChange={setPrototype} aria-label="Prototype edit" />
        <span>Prototype edit</span>
        <Badge variant="secondary">{prototype ? "prototype" : "read-only"}</Badge>
        {showDemo && (
          <Badge variant="outline" data-testid="mind-canvas-demo-badge">
            Demo fixture
          </Badge>
        )}
        {status === "live" && inProject && (
          <Badge variant="outline" data-testid="mind-canvas-live-badge">
            live
          </Badge>
        )}
      </div>
      {prototype && (
        <Alert className="mb-3 border-border bg-card">
          <AlertTitle>Prototype edit</AlertTitle>
          <AlertDescription>
            Edits would propose a Git patch. <strong>Propose Git patch is a stub</strong> (no POST / no patch file).
            Never write xyflow coordinates back to design.mind. Hardware SoT remains KiCad.
          </AlertDescription>
        </Alert>
      )}
      <div className="overflow-hidden rounded-lg border border-border" style={{ height: 520 }}>
        {status === "loading" && (
          <div
            className="flex h-full flex-col justify-center gap-3 p-6"
            data-testid="mind-canvas-loading"
            role="status"
          >
            <Skeleton className="h-8 w-40" />
            <Skeleton className="h-full min-h-[280px] w-full" />
          </div>
        )}
        {status === "empty" && inProject && (
          <div
            className="flex h-full flex-col items-center justify-center px-6 text-center"
            data-testid="mind-canvas-empty"
          >
            <p className="mb-1 text-sm font-medium text-foreground">No mind scene yet</p>
            <p className="max-w-lg text-sm text-muted-foreground">{emptyMessage || EMPTY_COPY}</p>
            {hasDesignMind ? (
              <>
                <p className="mt-2 max-w-lg text-xs text-muted-foreground">
                  design.mind is on HEAD. Open Think and compile a live scene with{" "}
                  <span className="font-mono">mind_scene</span> — no silent demo while waiting.
                </p>
                <Button
                  className="mt-4"
                  size="sm"
                  data-testid="mind-cta-compile"
                  onClick={() => requestOpenThink()}
                >
                  Open Think dock
                </Button>
              </>
            ) : showGenerateCtas ? (
              <>
                <p className="mt-2 max-w-lg text-xs text-muted-foreground">
                  No design.mind on HEAD yet. Open Think to generate it from the hosted schematic, or ask
                  Think to distill the schematic into mind.
                </p>
                <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                  <Button size="sm" data-testid="mind-cta-think-design" onClick={() => requestOpenThink()}>
                    用 Think 生成 design.mind
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid="mind-cta-distill"
                    onClick={() => requestOpenThink()}
                  >
                    从原理图蒸馏 mind
                  </Button>
                </div>
              </>
            ) : (
              <p className="mt-2 max-w-lg text-xs text-muted-foreground">
                No design.mind on HEAD yet. Upload a KiCad revision on Files, then generate design.mind
                with Think.
              </p>
            )}
          </div>
        )}
        {status === "error" && inProject && (
          <div
            className="flex h-full flex-col items-center justify-center px-6 text-center"
            data-testid="mind-canvas-error"
          >
            <p className="mb-1 text-sm font-medium text-foreground">Mind scene failed to load</p>
            <p className="max-w-lg text-sm text-muted-foreground">{err || "unknown error"}</p>
          </div>
        )}
        {liveScene && (
          <ErrorBoundary
            fallback={
              <div className="flex h-full items-center justify-center p-6 text-sm text-muted-foreground">
                Canvas failed to mount. Check that React / @xyflow/react are deduped.
              </div>
            }
          >
            <div className="h-full" data-testid={showDemo ? "mind-canvas-demo" : "mind-canvas-live"}>
              <MindCanvas
                scene={liveScene}
                pageMode="architecture"
                nodesDraggable={prototype}
                onSelect={(mod) => setSelected(mod.name ?? null)}
                onNodesChange={() => {
                  /* session pose only — not persisted to design.mind */
                }}
              />
            </div>
          </ErrorBoundary>
        )}
      </div>
      <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
        <span>
          Selected: <span className="font-mono text-foreground">{selected ?? "—"}</span> · @xyflow/react / @rscircuit/mind-canvas
        </span>
        {prototype && (
          <Button
            size="sm"
            variant="outline"
            disabled
            title="Stub: no git patch POST in this PR"
            aria-label="Propose Git patch (stub)"
          >
            Propose Git patch (stub)
          </Button>
        )}
      </div>
    </div>
  );
}

export function MindPage() {
  return (
    <AppShell
      title={
        <PageTitle kicker="Agents · Circuit Mind">
          Mind canvas <Badge variant="secondary">read-only</Badge>
        </PageTitle>
      }
    >
      <MindCanvasPanel />
    </AppShell>
  );
}
